import { clienteKey } from '@/domain/common/helpers/clienteFiltro';
import { EnrichedOp, FonteTempo, RiscoFinanceiro, RiscoTipo } from '@/domain/ops/interfaces/ops';

import { parseIsoDate } from './formatters';
import { entregaDaOp, RiscoFiltro } from './opsFilters';

/** The source that is a guess, not a measurement: most of the portfolio runs on it today. */
const FONTE_ESTIMADA: FonteTempo = 'SEQUENCIA_ESTIMADA';

const MS_POR_DIA = 24 * 60 * 60 * 1000;

/** One OP at risk, with the billing it puts at stake and how late it lands. */
export interface OpEmRisco {
  op: EnrichedOp;
  tipo: RiscoTipo;
  /** The deadline that counts: the renegotiated date wins over the original one. */
  prazo: string;
  /** The billing of this OP, the SAME number the Faturamento card adds up. */
  valor: number;
  diasAtraso: number;
}

export const RISCO_ZERADO: RiscoFinanceiro = {
  valor: 0,
  ops: 0,
  jaVencido: 0,
  vaiAtrasar: 0,
  comTempoEstimado: 0,
  semPrevisao: 0,
  opsSemPrevisao: 0,
  opsSemPrevisaoForaDaCostura: 0,
  opsSemPrevisaoSemTempo: 0,
  opsSemPrevisaoSemCelula: 0,
};

/**
 * A OP que o motor NÃO conseguiu prometer: tem prazo, o prazo ainda não passou e mesmo
 * assim não há data ao cliente, porque não há tempo por peça para calcular uma.
 *
 * Ela sumia do risco: `riscoDaOp` precisa comparar a data ao cliente com o prazo, e sem
 * data a comparação é falsa. Sumir é a pior resposta, porque essas são justamente as
 * que ninguém consegue garantir. Espelha `opSemPrevisao` da API.
 */
export function opSemPrevisao(op: EnrichedOp, hoje: string): boolean {
  const prazo = entregaDaOp(op);
  return prazo !== null && prazo >= hoje && (op.dataAoCliente ?? null) === null;
}

/**
 * O QUE impediu a promessa, na ordem em que um motivo explica o outro. Espelha
 * `motivoSemPrevisao` da API: fora da costura ganha de tudo (a peça é costurada fora ou
 * já está pronta, então a OP não ocupa célula e não recebe data por célula, tendo ou
 * não minuto por peça), depois vem a falta de tempo, e o resto é a OP que tem tempo e
 * está na costura e mesmo assim não coube em nenhuma célula.
 */
export type SemPrevisaoMotivo = 'FORA_DA_COSTURA' | 'SEM_TEMPO' | 'SEM_CELULA';

export function motivoSemPrevisao(op: EnrichedOp): SemPrevisaoMotivo {
  if ((op.foraDaCostura ?? null) !== null) {
    return 'FORA_DA_COSTURA';
  }
  if ((op.tempo?.minutoPeca ?? null) === null) {
    return 'SEM_TEMPO';
  }
  return 'SEM_CELULA';
}

/** O contador de cada motivo dentro do risco, na ordem em que a frase os lista. */
const CONTADOR_DO_MOTIVO: Readonly<
  Record<SemPrevisaoMotivo, 'opsSemPrevisaoForaDaCostura' | 'opsSemPrevisaoSemTempo' | 'opsSemPrevisaoSemCelula'>
> = {
  FORA_DA_COSTURA: 'opsSemPrevisaoForaDaCostura',
  SEM_TEMPO: 'opsSemPrevisaoSemTempo',
  SEM_CELULA: 'opsSemPrevisaoSemCelula',
};

/** Uma parte da abertura: o motivo e quantas OPs ele responde. */
export interface ISemPrevisaoParte {
  motivo: SemPrevisaoMotivo;
  n: number;
}

/**
 * A abertura do "sem previsão" com SÓ os motivos que existem hoje: listar um motivo em
 * zero faria a frase afirmar o que não aconteceu.
 */
export function aberturaSemPrevisao(risco: RiscoFinanceiro): Array<ISemPrevisaoParte> {
  return (Object.keys(CONTADOR_DO_MOTIVO) as Array<SemPrevisaoMotivo>)
    .map((motivo) => ({ motivo, n: risco[CONTADOR_DO_MOTIVO[motivo]] ?? 0 }))
    .filter((parte) => parte.n > 0);
}

function diasEntre(de: string, ate: string): number {
  const inicio = parseIsoDate(de);
  const fim = parseIsoDate(ate);
  if (inicio === null || fim === null) {
    return 0;
  }
  return Math.round((fim.getTime() - inicio.getTime()) / MS_POR_DIA);
}

/**
 * How late the OP lands, in calendar days from its deadline. An OP whose deadline has
 * passed is at least today late, even when the engine could not give it a date to the
 * client, so the column never reads zero on an OP that is already overdue.
 */
function atrasoEmDias(prazo: string, dataAoCliente: string | null, hoje: string): number {
  const previsao = dataAoCliente !== null && dataAoCliente > hoje ? dataAoCliente : hoje;
  return Math.max(0, diasEntre(prazo, previsao));
}

/**
 * Whether this OP will NOT be delivered on time, by the same rule the Portal calls
 * "Atrasada" (`risco-financeiro.util.ts` on the API): the date the system gives the client
 * falls after the order's deadline. An OP whose deadline has already passed and that is
 * still in the portfolio is at risk too, whatever the engine projects for it.
 *
 * An OP with no deadline cannot be shown to be late, so it answers `null`. Only the
 * portfolio is asked: an OP that left or was deleted never reaches here.
 */
export function riscoDaOp(op: EnrichedOp, hoje: string): OpEmRisco | null {
  const prazo = entregaDaOp(op);
  if (prazo === null) {
    return null;
  }
  const vencido = prazo < hoje;
  const dataAoCliente = op.dataAoCliente ?? null;
  if (!vencido && (dataAoCliente === null || dataAoCliente <= prazo)) {
    return null;
  }
  return {
    op,
    tipo: vencido ? 'JA_VENCIDO' : 'VAI_ATRASAR',
    prazo,
    // An absent field never sums NaN: this number lands on a card the directors read.
    valor: op.faturamento ?? 0,
    diasAtraso: atrasoEmDias(prazo, dataAoCliente, hoje),
  };
}

/** The OPs at risk of the list received, in the order it came. */
export function opsEmRisco(ops: Array<EnrichedOp>, hoje: string): Array<OpEmRisco> {
  return ops.flatMap((op) => {
    const risco = riscoDaOp(op, hoje);
    return risco === null ? [] : [risco];
  });
}

/**
 * The risk chip of the filter bar. It lives here and not with the other filters because it
 * needs today's date, which the filters do not carry: the screen composes the two.
 */
export function matchesRiscoFiltro(op: EnrichedOp, risco: RiscoFiltro | '', hoje: string): boolean {
  if (risco === '') {
    return true;
  }
  const emRisco = riscoDaOp(op, hoje);
  if (emRisco === null) {
    return false;
  }
  return risco === 'EM_RISCO' || emRisco.tipo === risco;
}

/** The biggest exposure first, which is the order whoever reads the list is looking for. */
function porMaiorValor<T extends { valor: number }>(itens: Array<T>): Array<T> {
  return [...itens].sort((a, b) => b.valor - a.valor);
}

/** The same rows the table already holds, biggest exposure first. */
export function ordenarPorRisco<T extends EnrichedOp>(ops: Array<T>, hoje: string): Array<T> {
  const comValor = ops.map((op) => ({ op, valor: riscoDaOp(op, hoje)?.valor ?? 0 }));
  return porMaiorValor(comValor).map((item) => item.op);
}

/**
 * A carteira pelo MAIOR atraso. O número vem do motor (`diasAtraso`), o mesmo que a
 * coluna mostra e que a Fila lê, então ordenar não pode discordar do que está escrito.
 * Empate desfeito pelo prazo mais antigo: entre dois atrasos iguais, quem foi prometido
 * primeiro vem primeiro.
 */
export function ordenarPorAtraso<T extends EnrichedOp>(ops: Array<T>): Array<T> {
  return [...ops].sort(
    (a, b) =>
      (b.diasAtraso ?? 0) - (a.diasAtraso ?? 0) ||
      (entregaDaOp(a) ?? '').localeCompare(entregaDaOp(b) ?? ''),
  );
}

/** One client of the risk list, with its own totals and the OPs behind them. */
export interface RiscoPorCliente {
  /** The client as the rows spell it, the SAME field the "Cliente" filter reads. */
  cliente: string;
  /** Brands of this client inside the risk: one client can carry several. */
  marcas: Array<string>;
  ops: Array<OpEmRisco>;
  valor: number;
  jaVencido: number;
  vaiAtrasar: number;
  maiorAtraso: number;
}

/**
 * The risk by client, biggest exposure first, with the OPs of each one already ordered the
 * same way. Groups by the same key the "Cliente" filter compares by, so opening a client
 * here and filtering by it on the bar bring exactly the same OPs.
 */
export function riscoPorCliente(emRisco: Array<OpEmRisco>): Array<RiscoPorCliente> {
  const porChave = new Map<string, RiscoPorCliente>();

  for (const item of emRisco) {
    const cliente = (item.op.cliente ?? '').trim();
    const chave = clienteKey(cliente);
    const atual = porChave.get(chave) ?? {
      cliente,
      marcas: [],
      ops: [],
      valor: 0,
      jaVencido: 0,
      vaiAtrasar: 0,
      maiorAtraso: 0,
    };
    atual.ops.push(item);
    atual.valor += item.valor;
    if (item.tipo === 'JA_VENCIDO') {
      atual.jaVencido += item.valor;
    } else {
      atual.vaiAtrasar += item.valor;
    }
    atual.maiorAtraso = Math.max(atual.maiorAtraso, item.diasAtraso);
    const marca = (item.op.marca ?? '').trim();
    if (marca !== '' && !atual.marcas.includes(marca)) {
      atual.marcas.push(marca);
    }
    porChave.set(chave, atual);
  }

  return porMaiorValor([...porChave.values()]).map((grupo) => ({
    ...grupo,
    marcas: [...grupo.marcas].sort((a, b) => a.localeCompare(b, 'pt-BR')),
    ops: porMaiorValor(grupo.ops),
  }));
}

/**
 * The risk as the card shows it. `jaVencido` and `vaiAtrasar` split the total in two and
 * close with it; `comTempoEstimado` cuts the SAME total the other way, so it is not a
 * third slice.
 */
export function resumoDoRisco(
  emRisco: Array<OpEmRisco>,
  /** A carteira inteira e o dia: é dela que saem as OPs sem previsão, que não estão em `emRisco`. */
  ops: Array<EnrichedOp> = [],
  hoje = '',
): RiscoFinanceiro {
  const resumo: RiscoFinanceiro = { ...RISCO_ZERADO };

  for (const op of ops) {
    if (hoje !== '' && opSemPrevisao(op, hoje)) {
      resumo.semPrevisao += op.faturamento ?? 0;
      resumo.opsSemPrevisao += 1;
      const contador = CONTADOR_DO_MOTIVO[motivoSemPrevisao(op)];
      resumo[contador] = (resumo[contador] ?? 0) + 1;
    }
  }

  for (const item of emRisco) {
    resumo.ops += 1;
    resumo.valor += item.valor;
    if (item.tipo === 'JA_VENCIDO') {
      resumo.jaVencido += item.valor;
    } else {
      resumo.vaiAtrasar += item.valor;
    }
    if (item.op.tempo?.fonte === FONTE_ESTIMADA) {
      resumo.comTempoEstimado += item.valor;
    }
  }

  return resumo;
}
